import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Activity, AlertTriangle, Clock, Plus, Shield, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";

import { DashboardShell } from "@/components/dravian/dashboard-shell";
import { Eyebrow } from "@/components/dravian/eyebrow";
import { team as teamApi, type TeamMember } from "@/lib/api";

export const Route = createFileRoute("/team")({
  head: () => ({
    meta: [
      { title: "Team — Dravian Horizon" },
      { name: "description", content: "Manage analysts, roles, and access across your Dravian deployment." },
    ],
  }),
  component: TeamPage,
});

function TeamPage() {
  const qc = useQueryClient();
  const teamQ = useQuery({ queryKey: ["team"], queryFn: () => teamApi.summary() });
  const activityQ = useQuery({
    queryKey: ["team-activity"],
    queryFn: () => teamApi.activity(50),
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const invite = useMutation({
    mutationFn: (payload: { email: string; name?: string; role: "admin" | "analyst" | "viewer" }) =>
      teamApi.invite(payload),
    onSettled: () => qc.invalidateQueries({ queryKey: ["team"] }),
  });

  const members = teamQ.data?.members ?? [];
  const roleCounts = countRoles(members);
  const seatsTotal = teamQ.data?.seats_total;
  const seatsUsed = teamQ.data?.seats_used ?? members.length;

  return (
    <DashboardShell>
      <main className="flex-1 mx-auto w-full max-w-[1400px] px-6 py-8 space-y-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow mb-1">Organization</div>
            <h1 className="text-[28px] font-semibold tracking-tight">Team</h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              Licensed users, local roles, and workspace activity for this deployment.
            </p>
          </div>
          <button
            onClick={() => setInviteOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-[13px] font-medium hover:bg-primary/90 glow-primary"
          >
            <Plus className="h-4 w-4" /> Invite member
          </button>
        </div>

        {inviteOpen && (
          <InvitePanel
            busy={invite.isPending}
            error={invite.isError ? String(invite.error) : null}
            onSubmit={(payload) => invite.mutate(payload)}
          />
        )}

        <div className="grid lg:grid-cols-4 gap-3">
          <Stat label="Seats used" value={seatsTotal ? `${seatsUsed}/${seatsTotal}` : String(seatsUsed)} icon={Users} />
          <Stat label="Admins" value={(roleCounts.admin ?? 0) + (roleCounts.owner ?? 0)} icon={Shield} />
          <Stat label="Operators" value={(roleCounts.operator ?? 0) + (roleCounts.analyst ?? 0)} icon={Activity} />
          <Stat label="Viewers" value={roleCounts.viewer ?? 0} icon={Clock} />
        </div>

        {teamQ.isError && (
          <div className="rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high flex gap-2">
            <AlertTriangle className="h-3.5 w-3.5" /> {String(teamQ.error)}
          </div>
        )}

        <section className="panel">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <Eyebrow count={members.length}>Members</Eyebrow>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Users className="h-3.5 w-3.5" /> {seatsUsed} active local users
            </div>
          </div>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left font-medium px-5 py-2.5">User</th>
                <th className="text-left font-medium px-5 py-2.5">Role</th>
                <th className="text-left font-medium px-5 py-2.5">Workspaces</th>
                <th className="text-left font-medium px-5 py-2.5">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.map((m) => (
                <tr key={m.id} className="hover:bg-surface/40 transition">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar email={m.email} />
                      <div>
                        <div className="font-medium">{m.email}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">
                          seat {m.seat_id ? m.seat_id.slice(0, 8) : "local"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3"><RolePill role={m.role} /></td>
                  <td className="px-5 py-3 font-mono text-[12px] text-muted-foreground">{m.workspace_count}</td>
                  <td className="px-5 py-3 font-mono text-[12px] text-muted-foreground">{relative(m.last_seen_at)}</td>
                </tr>
              ))}
              {!teamQ.isLoading && members.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-[12px] text-muted-foreground">
                    Users appear here after their first login to this Horizon deployment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="panel p-5">
          <Eyebrow count={activityQ.data?.length ?? 0}>Recent Activity</Eyebrow>
          <div className="mt-2 divide-y divide-border">
            {(activityQ.data ?? []).slice(0, 12).map((event) => (
              <div key={event.id} className="py-2.5 flex items-center justify-between gap-4 text-[12px]">
                <div className="min-w-0">
                  <span className="text-foreground/90">{event.action.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground"> · {event.object_type}</span>
                  {event.actor_email && <span className="text-muted-foreground"> · {event.actor_email}</span>}
                </div>
                <span className="font-mono text-[11px] text-muted-foreground shrink-0">{relative(event.created_at)}</span>
              </div>
            ))}
            {!activityQ.isLoading && (activityQ.data ?? []).length === 0 && (
              <p className="py-4 text-[12px] text-muted-foreground">No audited activity yet.</p>
            )}
          </div>
        </section>
      </main>
    </DashboardShell>
  );
}

function InvitePanel({
  busy,
  error,
  onSubmit,
}: {
  busy: boolean;
  error: string | null;
  onSubmit: (payload: { email: string; name?: string; role: "admin" | "analyst" | "viewer" }) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"admin" | "analyst" | "viewer">("analyst");
  return (
    <section className="panel p-5 space-y-3">
      <Eyebrow>Invite Member</Eyebrow>
      <div className="grid md:grid-cols-[1fr_1fr_160px_auto] gap-2">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@agency.gov" className="rounded-md border border-border bg-surface-elevated px-3 py-2 text-[13px] outline-none focus:border-primary/50" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded-md border border-border bg-surface-elevated px-3 py-2 text-[13px] outline-none focus:border-primary/50" />
        <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} className="rounded-md border border-border bg-surface px-3 py-2 text-[13px]">
          <option value="analyst">Analyst</option>
          <option value="admin">Admin</option>
          <option value="viewer">Viewer</option>
        </select>
        <button
          onClick={() => onSubmit({ email, name, role })}
          disabled={!email.trim() || busy}
          className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-[13px] font-medium disabled:opacity-50"
        >
          {busy ? "Sending..." : "Send"}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Invitations are routed through Dravian License Service so Horizon never receives AWS IAM credentials.
      </p>
      {error && <p className="text-[11px] text-risk-high">{error}</p>}
    </section>
  );
}

function Stat({ label, value, icon: Icon }: { label: string; value: string | number; icon: LucideIcon }) {
  return (
    <div className="panel p-4">
      <div className="flex items-start justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="mt-3 text-[32px] font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function RolePill({ role }: { role: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase">
      {role}
    </span>
  );
}

function Avatar({ email }: { email: string }) {
  const initials = email.slice(0, 2).toUpperCase();
  return <div className="h-8 w-8 rounded-full bg-primary/20 text-primary grid place-items-center text-[11px] font-semibold">{initials}</div>;
}

function countRoles(members: TeamMember[]) {
  return members.reduce<Record<string, number>>((acc, m) => {
    acc[m.role] = (acc[m.role] ?? 0) + 1;
    return acc;
  }, {});
}

function relative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.round(diff / 60_000));
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}
